import { useCallback, useEffect } from 'react';

import type { ViewState } from '../shared/types';

interface Boundary {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface UseAutoFitProps {
  boundaries: Array<{ bounds: Boundary }>;
  canvasSize: { width: number; height: number };
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  autoRun?: boolean; // Auto-fit on initial load/boundary changes
}

interface UseAutoFitReturn {
  autoFit: () => void;
}

export const useAutoFit = ({
  boundaries,
  canvasSize,
  setViewState,
  autoRun = true
}: UseAutoFitProps): UseAutoFitReturn => {
  const autoFit = useCallback(() => {
    if (boundaries.length === 0 || !canvasSize.width || !canvasSize.height) {
      // No boundaries or canvas not ready - reset to default
      setViewState({
        offsetX: 0,
        offsetY: 0,
        scale: 1,
        rotation: 0
      });
      return;
    }

    // Calculate bounding box of all boundaries
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    boundaries.forEach(boundary => {
      minX = Math.min(minX, boundary.bounds.minX);
      minY = Math.min(minY, boundary.bounds.minY);
      maxX = Math.max(maxX, boundary.bounds.maxX);
      maxY = Math.max(maxY, boundary.bounds.maxY);
    });

    const worldWidth = maxX - minX;
    const worldHeight = maxY - minY;
    const worldCenterX = (minX + maxX) / 2;
    const worldCenterY = (minY + maxY) / 2;

    const canvasCenterX = canvasSize.width / 2;
    const canvasCenterY = canvasSize.height / 2;

    // Calculate scale to fit all content with 90% margin
    const scaleX = (canvasSize.width * 0.9) / worldWidth;
    const scaleY = (canvasSize.height * 0.9) / worldHeight;
    const scale = Math.min(scaleX, scaleY, 2); // Cap at 2x zoom

    setViewState({
      scale: scale,
      offsetX: (canvasCenterX / scale) - worldCenterX,
      offsetY: (canvasCenterY / scale) - worldCenterY,
      rotation: 0 // Reset rotation when auto-fitting
    });
  }, [boundaries, canvasSize, setViewState]);

  // Auto-fit on initial load when boundaries and canvas are ready
  useEffect(() => {
    if (autoRun && boundaries.length > 0 && canvasSize.width > 0 && canvasSize.height > 0) {
      autoFit();
    }
  }, [autoRun, boundaries, canvasSize.width, canvasSize.height, autoFit]);

  return {
    autoFit
  };
};
